<template>
    <div class="mojiscan-page">
        <header class="page-header">
            <div class="lang-switch">
                <a :class="{ active: $lang === 'ja' }" @click="switchLang('ja')">
                    <img :src="flagJa" class="lang-flag" alt="日本語"> 日本語
                </a>
                <span class="lang-sep">|</span>
                <a :class="{ active: $lang === 'en' }" @click="switchLang('en')">
                    <img :src="flagEn" class="lang-flag" alt="English"> English
                </a>
            </div>
            <h1 class="page-title">
                <img :src="titleUrl" class="title-image" :alt="$t('mojiscan.title')" />
            </h1>
            <div class="header-badge">
                <a :href="badgeLink" target="_blank" rel="noopener nofollow" @click="track('click', 'startup_accelerator_badge')">
                    <img :src="badgeImgUrl" alt="Scan &amp; PDF Tool, Featured on StartupAccelerator" width="250" height="54" />
                </a>
            </div>
        </header>

        <main class="page-main">
            <section class="hero">
                <p class="hero-badge">{{ $t('mojiscan.heroBadge') }}</p>
                <h2 class="hero-title">{{ $t('mojiscan.tagline') }}</h2>
                <p class="hero-sub">{{ $t('mojiscan.subline') }}</p>
                <div class="hero-actions">
                    <a :href="purchaseUrl" target="_blank" rel="noopener" class="purchase-link" @click="track('click', 'purchase')">
                        <i class="el-icon-shopping-cart-full"></i>
                        {{ $t('common.purchaseLabel') }}
                    </a>
                    <a :href="downloadUrl" class="download-link hero-download" @click="track('click', 'download_trial')">
                        <i class="el-icon-download"></i>
                        {{ $t('common.trialDownload') }}
                    </a>
                </div>
                <p class="hero-note">{{ $t('mojiscan.heroNote', { trialDays: trialDays, trialPagesPerDay: trialPagesPerDay, price: price }) }}</p>
            </section>

            <section v-if="introParagraphs.length" class="intro-section">
                <h2 class="section-title">{{ $t('mojiscan.introTitle') }}</h2>
                <div class="intro-copy">
                    <p v-for="(paragraph, idx) in introParagraphs" :key="idx">{{ paragraph }}</p>
                </div>
            </section>

            <h2 class="section-title">{{ $t('mojiscan.featuresTitle') }}</h2>
            <div class="feature-cards">
                <div class="feature-card" v-for="(card, idx) in $t('mojiscan.featureCards')" :key="idx">
                    <i :class="card.icon"></i>
                    <h3>{{ card.title }}</h3>
                    <p>{{ card.desc }}</p>
                </div>
            </div>
            <ul class="feature-list">
                <li v-for="(item, index) in featureList" :key="index">{{ item }}</li>
            </ul>
            <p class="note">{{ $t('mojiscan.disclaimer') }}</p>

            <h2 class="section-title">{{ $t('mojiscan.screenshotTitle') }}</h2>
            <div class="screenshot-box">
                <img v-if="screenshotShown" :src="screenshotUrl" :alt="$t('mojiscan.screenshotAlt')" @error="screenshotShown = false" />
                <p v-else class="screenshot-placeholder">{{ $t('mojiscan.screenshotPlaceholder') }}</p>
            </div>

            <h2 class="section-title">{{ $t('mojiscan.priceTitle') }}</h2>
            <p class="price">{{ $t('mojiscan.priceLine', { price: price, trialDays: trialDays }) }}</p>
            <p class="purchase-row">
                <a :href="purchaseUrl" target="_blank" rel="noopener" class="purchase-link" @click="track('click', 'purchase')">
                    <i class="el-icon-shopping-cart-full"></i>
                    {{ $t('common.purchaseLabel') }}
                </a>
            </p>

            <h2 class="section-title">{{ $t('mojiscan.downloadTitle') }}</h2>
            <p class="download-desc" v-html="$t('mojiscan.downloadDesc')"></p>
            <p>
                <a :href="downloadUrl" class="download-link" @click="track('click', 'download_trial')">
                    {{ $t('mojiscan.downloadLink', { version: version, zipName: zipName }) }}
                </a>
            </p>

            <h2 class="section-title">{{ $t('mojiscan.faqTitle') }}</h2>
            <div class="faq-item" v-for="(item, idx) in $t('mojiscan.faqItems')" :key="idx">
                <details>
                    <summary>{{ item.q }}</summary>
                    <p>{{ item.a }}</p>
                </details>
            </div>

            <h2 class="section-title">{{ $t('mojiscan.sellerTitle') }}</h2>
            <ul class="seller-info">
                <li v-for="(item, idx) in $t('mojiscan.sellerInfo')" :key="idx">{{ item }}</li>
            </ul>

            <h2 class="section-title">{{ $t('mojiscan.manualTitle') }}</h2>
            <p class="manual-row">
                <a :href="manualJaUrl" target="_blank" rel="noopener" class="download-link" @click="track('click', 'manual_ja')">
                    <i class="el-icon-document"></i> {{ $t('mojiscan.manualJa') }}
                </a>
                <a :href="manualUrl" target="_blank" rel="noopener" class="download-link" @click="track('click', 'manual_en')">
                    <i class="el-icon-document"></i> {{ $t('mojiscan.manualEn') }}
                </a>
            </p>
            <p class="note" v-html="$t('common.contact')"></p>
        </main>

        <footer class="page-footer">
            <p>{{ $t('common.copyright') }}</p>
        </footer>
    </div>
</template>

<script>
import flagJa from '../../assets/images/flag-jp.svg'
import flagEn from '../../assets/images/flag-gb.svg'

import {
  MOJISCAN_VERSION,
  MOJISCAN_ZIP_NAME,
  MOJISCAN_DOWNLOAD_URL,
  MOJISCAN_PURCHASE_URL,
  MOJISCAN_BADGE_LINK,
  MOJISCAN_BADGE_IMG_URL,
  MOJISCAN_TRIAL_DAYS,
  MOJISCAN_TRIAL_PAGES_PER_DAY,
  MOJISCAN_TITLE_URL,
  MOJISCAN_SCREENSHOT_URL,
  MOJISCAN_MANUAL_URL,
  MOJISCAN_MANUAL_JA_URL
} from './mojiscanConst'

export default {
  data () {
    return {
      version: MOJISCAN_VERSION,
      zipName: MOJISCAN_ZIP_NAME,
      downloadUrl: MOJISCAN_DOWNLOAD_URL,
      purchaseUrl: MOJISCAN_PURCHASE_URL,
      badgeLink: MOJISCAN_BADGE_LINK,
      badgeImgUrl: MOJISCAN_BADGE_IMG_URL,
      trialDays: MOJISCAN_TRIAL_DAYS,
      trialPagesPerDay: MOJISCAN_TRIAL_PAGES_PER_DAY,
      titleUrl: MOJISCAN_TITLE_URL,
      screenshotUrl: MOJISCAN_SCREENSHOT_URL,
      manualUrl: MOJISCAN_MANUAL_URL,
      manualJaUrl: MOJISCAN_MANUAL_JA_URL,
      flagJa: flagJa,
      flagEn: flagEn,
      screenshotShown: true
    }
  },
  computed: {
    price () {
      return this.$t('mojiscan.price')
    },
    introParagraphs () {
      return this.$t('mojiscan.introParagraphs') || []
    },
    featureList () {
      const params = { trialDays: this.trialDays, trialPagesPerDay: this.trialPagesPerDay }
      return [this.$t('mojiscan.feature'), ...this.$t('mojiscan.featureList', params)]
    }
  },
  created () {
    this.applyLanguageFromQuery()
  },
  mounted () {
    this.applyLanguageFromQuery()
    this.injectJsonLd()
  },
  watch: {
    '$route.query.lang' () {
      this.applyLanguageFromQuery()
    }
  },
  methods: {
    track (action, label) {
      if (window.gtag) {
        window.gtag('event', action, { event_category: 'mojiscan', event_label: label })
      }
      if (window.fbq) {
        window.fbq('trackCustom', 'MojiScan_' + action, { label: label })
      }
    },
    getLanguageParam () {
      const routeLang = this.$route && this.$route.query ? this.$route.query.lang : ''
      if (Array.isArray(routeLang)) {
        return routeLang[0] || ''
      }
      if (routeLang) {
        return routeLang
      }
      if (typeof window === 'undefined') {
        return ''
      }
      const match = window.location.search.match(/[?&]lang=(ja|en)(?:&|$)/i)
      return match ? match[1] : ''
    },
    applyLanguageFromQuery () {
      const lang = String(this.getLanguageParam() || '').toLowerCase()
      if (lang === 'ja' || lang === 'en') {
        this.$switchLang(lang)
      }
    },
    switchLang (lang) {
      this.$switchLang(lang)
      const currentLang = Array.isArray(this.$route.query.lang) ? this.$route.query.lang[0] : this.$route.query.lang
      if (currentLang === lang) {
        return
      }
      const query = Object.assign({}, this.$route.query, { lang: lang })
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
    injectJsonLd () {
      const existing = document.getElementById('mojiscan-jsonld')
      if (existing) existing.remove()
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.id = 'mojiscan-jsonld'
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: this.$t('mojiscan.title'),
        alternateName: 'MojiScan',
        url: 'https://www.searchplusplus.jp/#/mojiscan',
        description: this.$t('mojiscan.subline'),
        operatingSystem: 'Windows 10, Windows 11 (64-bit)',
        applicationCategory: 'BusinessApplication',
        offers: {
          '@type': 'Offer',
          price: this.$t('mojiscan.price').replace(/[^\d]/g, ''),
          priceCurrency: 'JPY'
        }
      })
      document.head.appendChild(script)
    }
  }
}
</script>

<style scoped>
.mojiscan-page {
    max-width: 900px;
    margin: 0 auto;
    padding: 24px 20px 40px;
    min-height: 100vh;
    background: #fff;
}

.page-header {
    text-align: center;
    padding-bottom: 24px;
    border-bottom: 2px solid #0c86e8;
    margin-bottom: 32px;
}

.page-header h1 {
    clear: both;
    margin: 0 0 16px;
    color: #0c86e8;
    font-size: 28px;
}

.title-image {
    display: block;
    width: 100%;
    max-width: 320px;
    height: auto;
    margin: 0 auto;
}

.lang-switch {
    float: right;
    margin-top: 6px;
    font-size: 13px;
}

.lang-switch a {
    cursor: pointer;
    color: #909399;
    text-decoration: none;
    font-weight: 500;
}

.lang-switch a:hover,
.lang-switch a.active {
    color: #0c86e8;
}

.lang-flag {
    width: 19px;
    height: 13px;
    vertical-align: -2px;
    margin-right: 4px;
    border-radius: 2px;
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.25);
}

.lang-sep {
    margin: 0 6px;
    color: #dcdfe6;
}

.header-badge {
    margin: 0 0 16px;
}

.header-badge a {
    display: inline-block;
    line-height: 0;
}

.header-badge img {
    width: 250px;
    max-width: 100%;
    height: auto;
}

.page-main {
    line-height: 1.8;
    color: #303133;
}

.price {
    font-size: 18px;
    font-weight: 700;
    color: #0c86e8;
    margin: 0 0 16px;
}

.purchase-row {
    margin: 0 0 8px;
}

.purchase-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: #0c86e8;
    color: #fff;
    text-decoration: none;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 700;
    box-shadow: 0 2px 8px rgba(12, 134, 232, 0.35);
    transition: background 0.2s ease, transform 0.2s ease;
}

.purchase-link i {
    font-size: 20px;
}

.purchase-link:hover {
    background: #0a6bc0;
    transform: translateY(-1px);
    color: #fff;
}

.section-title {
    font-size: 20px;
    color: #2c3e50;
    margin: 28px 0 12px;
}

.feature-list {
    padding-left: 24px;
    margin: 0;
}

.download-desc {
    margin: 0 0 12px;
}

.download-link {
    display: inline-block;
    margin: 8px 0;
    padding: 10px 16px;
    background: #0c86e8;
    color: #fff;
    text-decoration: none;
    border-radius: 6px;
    font-weight: 600;
}

.download-link:hover {
    background: #0a6bc0;
}

.manual-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin: 8px 0;
}

.manual-row .download-link i {
    margin-right: 6px;
}

.note {
    font-size: 14px;
    color: #606266;
    margin-top: 20px;
}

.page-footer {
    margin-top: 48px;
    padding-top: 20px;
    border-top: 1px solid #ebeef5;
    text-align: center;
    color: #909399;
    font-size: 14px;
}

.hero {
    padding: 8px 0 24px;
    margin-bottom: 8px;
    text-align: center;
    border-bottom: 1px solid #ebeef5;
}

.hero-badge {
    display: inline-block;
    margin: 0 0 12px;
    padding: 4px 14px;
    background: #e9f4fe;
    color: #0a6bc0;
    border: 1px solid #b9def9;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 700;
}

.hero-title {
    margin: 0 0 12px;
    font-size: 30px;
    color: #0c86e8;
}

.hero-sub {
    margin: 0 0 20px;
    font-size: 16px;
    color: #303133;
}

.intro-section {
    margin-bottom: 8px;
}

.intro-copy p {
    margin: 0 0 14px;
    font-size: 15px;
}

.intro-copy p:last-child {
    margin-bottom: 0;
}

.hero-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
}

.hero-download {
    background: #fff;
    color: #0c86e8;
    border: 2px solid #0c86e8;
}

.hero-download:hover {
    background: #e9f4fe;
    color: #0a6bc0;
}

.hero-note {
    margin: 14px 0 0;
    font-size: 13px;
    color: #909399;
}

.feature-cards {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin: 0 0 16px;
}

.feature-card {
    flex: 1 1 220px;
    padding: 18px;
    background: #fafbfc;
    border: 1px solid #ebeef5;
    border-radius: 8px;
}

.feature-card i {
    display: block;
    margin-bottom: 8px;
    font-size: 26px;
    color: #0c86e8;
}

.feature-card h3 {
    margin: 0 0 8px;
    font-size: 16px;
    color: #2c3e50;
}

.feature-card p {
    margin: 0;
    font-size: 13px;
    line-height: 1.7;
    color: #606266;
}

.screenshot-box {
    padding: 20px;
    background: #fafbfc;
    border: 2px dashed #d3dce6;
    border-radius: 8px;
    text-align: center;
    color: #909399;
}

.screenshot-box img {
    max-width: 100%;
    border-radius: 6px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.screenshot-placeholder {
    margin: 0;
    font-size: 14px;
}

.faq-item {
    margin: 0 0 8px;
    border: 1px solid #ebeef5;
    border-radius: 6px;
}

.faq-item summary {
    padding: 12px 16px;
    cursor: pointer;
    font-weight: 600;
    color: #303133;
}

.faq-item details[open] summary {
    border-bottom: 1px solid #ebeef5;
}

.faq-item p {
    margin: 0;
    padding: 12px 16px;
    font-size: 14px;
    color: #606266;
}

.seller-info {
    margin: 0;
    padding-left: 20px;
    font-size: 14px;
    line-height: 2;
    color: #606266;
}
</style>
